import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowUpRight, Clock } from "lucide-react";
import { API, IS_STATIC, inr, slug, waLink } from "../constants/site";
import { Link } from "react-router-dom";
import { STATIC_PACKAGES } from "../constants/staticData";
import { ChapterHeader } from "./ChapterHeader";

const FILTERS = [
  { id: "all", label: "All Journeys", testid: "package-filter-all" },
  { id: "international", label: "International", testid: "package-filter-international" },
  { id: "domestic", label: "Domestic India", testid: "package-filter-domestic" },
];

const PackageCard = ({ p, i }) => (
  <motion.article
    initial={{ opacity: 0, y: 32 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.7, delay: (i % 3) * 0.1, ease: [0.16, 1, 0.3, 1] }}
    data-testid={`package-card-${slug(p.title)}`}
    className="group relative overflow-hidden rounded-3xl border border-white/10 bg-surface transition-shadow duration-500 hover:shadow-[0_0_40px_rgba(255,107,53,0.15)]"
  >
    <div className="relative h-80 overflow-hidden">
      <img
        src={p.image}
        alt={p.title}
        loading="lazy"
        className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-110"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-surface via-transparent to-transparent" />
      {p.tag && (
        <span className="absolute left-5 top-5 rounded-full bg-sunset px-4 py-1.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-ink">
          {p.tag}
        </span>
      )}
      <span className="absolute right-5 top-5 rounded-full border border-white/20 bg-black/40 px-4 py-1.5 text-[10px] uppercase tracking-[0.2em] text-white/80 backdrop-blur">
        {p.region === "international" ? "International" : "Domestic"}
      </span>
    </div>
    <div className="p-7">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="font-display text-2xl leading-tight md:text-3xl">{p.title}</h3>
          <p className="mt-1 text-sm text-white/50">{p.destination}</p>
        </div>
        <div className="shrink-0 text-right">
          <p className="text-[10px] uppercase tracking-[0.2em] text-white/40">From</p>
          <p className="font-display text-2xl text-sunset">{inr(p.price)}</p>
        </div>
      </div>
      <div className="mt-4 flex items-center gap-2 text-xs text-white/50">
        <Clock className="h-3.5 w-3.5 text-teal" />
        {p.duration}
      </div>
      <div className="mt-5 flex flex-wrap gap-2">
        {p.highlights.map((h) => (
          <span key={h} className="rounded-full border border-white/10 px-3 py-1 text-[11px] text-white/60">
            {h}
          </span>
        ))}
      </div>
      <Link
        to={`/package/${slug(p.title)}`}
        data-testid={`package-details-btn-${slug(p.title)}`}
        className="mt-7 inline-flex w-full items-center justify-center gap-2 rounded-full bg-sunset py-3.5 text-xs font-semibold uppercase tracking-[0.2em] text-ink transition-colors duration-300 hover:bg-coral"
      >
        View Full Itinerary
        <ArrowUpRight className="h-4 w-4" />
      </Link>
      <a
        href={waLink(`Hi Gautam Travel! I'm interested in the "${p.title}" package (${p.duration}, from ${inr(p.price)}). Please share the full itinerary.`)}
        target="_blank"
        rel="noreferrer"
        data-testid={`package-enquire-btn-${slug(p.title)}`}
        className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-full border border-white/15 py-3.5 text-xs font-medium uppercase tracking-[0.2em] text-white/80 transition-colors duration-300 hover:border-sunset hover:text-sunset"
      >
        Enquire on WhatsApp
        <ArrowUpRight className="h-4 w-4" />
      </a>
    </div>
  </motion.article>
);

export const PackagesSection = () => {
  const [packages, setPackages] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (IS_STATIC) {
      setPackages(STATIC_PACKAGES);
      setLoading(false);
      return;
    }
    axios
      .get(`${API}/packages`)
      .then((r) => setPackages(r.data))
      .catch(() => setPackages([]))
      .finally(() => setLoading(false));
  }, []);

  const visible = packages.filter((p) => filter === "all" || p.region === filter);

  return (
    <section id="destinations" data-testid="packages-section" className="relative bg-ink px-6 py-28 md:px-10 md:py-36">
      <div className="mx-auto max-w-7xl">
        <ChapterHeader
          index="01"
          title="Destinations"
          testid="packages-header"
          heading={
            <>
              Packaged journeys, <span className="italic text-sunset">priced honestly.</span>
            </>
          }
          blurb="Flights, stays, transfers and sightseeing bundled into one transparent price. Every itinerary is customisable — tell us your dates and we shape it around you."
        />

        <div className="mb-12 flex flex-wrap gap-3" data-testid="package-filters">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              data-testid={f.testid}
              className={`rounded-full px-6 py-2.5 text-xs font-medium uppercase tracking-[0.2em] transition-colors duration-300 ${
                filter === f.id
                  ? "bg-sunset text-ink"
                  : "border border-white/15 text-white/60 hover:border-white/40 hover:text-white"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {loading ? (
          <p className="py-20 text-sm uppercase tracking-[0.3em] text-white/40" data-testid="packages-loading">
            Curating journeys…
          </p>
        ) : (
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3" data-testid="packages-grid">
            {visible.map((p, i) => (
              <PackageCard key={p.id || p.title} p={p} i={i} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};
